import React from "react";
import { DisruptionExerciseContainer } from "@b2bportal/core-disruption";
import { useDisruptionStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  type AirlineCode,
  CoreDisruptionComponent,
  type ITimeRange,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import dayjs from "dayjs";
import { DisruptionExerciseCheckbox } from "../DisruptionExerciseCheckbox";
import { DisruptionExerciseTimeWindowSlider } from "../DisruptionExerciseTimeWindowSlider";
import { DisruptionExpandableContainer } from "../DisruptionExpandableContainer";
import defaultStyles from "./DisruptionExerciseShopFilter.module.scss";

export interface DisruptionExerciseShopFilterProps {
  origin: string;
  destination: string;
  stopsOptions: Array<number>;
  selectedStops: Array<number>;
  airlinesOptions: Array<RebookAirlineOptions>;
  selectedAirlines: Array<string>;
  handleStopsSelection: (selected: boolean, numberOfStops: number) => void;
  handleAirlineSelection: (
    selected: boolean,
    airline: RebookAirlineOptions
  ) => void;
  departureTimeRange: ITimeRange;
  arrivalTimeRange: ITimeRange;
  handleDepartureTimeChange: (min: number, max: number) => void;
  handleArrivalTimeChange: (min: number, max: number) => void;
  flightDurationRange: ITimeRange;
  handleFlightDurationChange: (min: number, max: number) => void;
  layoverDurationRange: ITimeRange;
  handleLayoverDurationChange: (min: number, max: number) => void;
  selectedLayoverDurationRange: ITimeRange;
  headerContent?: React.ReactNode;
  defaultFiltersExpanded?: boolean;
}

export interface RebookAirlineOptions {
  label: AirlineCode;
  value: AirlineCode;
}

export const DisruptionExerciseShopFilter = ({
  origin,
  destination,
  stopsOptions,
  selectedStops,
  airlinesOptions,
  selectedAirlines,
  handleStopsSelection,
  handleAirlineSelection,
  departureTimeRange,
  arrivalTimeRange,
  handleDepartureTimeChange,
  handleArrivalTimeChange,
  flightDurationRange,
  handleFlightDurationChange,
  layoverDurationRange,
  handleLayoverDurationChange,
  selectedLayoverDurationRange,
  headerContent,
  defaultFiltersExpanded,
}: DisruptionExerciseShopFilterProps) => {
  const { t, brand } = useI18nContext();
  const TIME_RANGE_MAX = 1439;
  const COMPONENT_KEY = CoreDisruptionComponent.DisruptionExerciseShopFilter;
  const styles = useDisruptionStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const airlinesFilter = airlinesOptions.map((airline) => {
    return (
      <DisruptionExerciseCheckbox
        label={airline.label}
        onChange={(selected) => {
          handleAirlineSelection(selected, airline);
        }}
        selected={selectedAirlines.includes(airline.value)}
        key={airline.label}
      />
    );
  });

  const stopsFilters = stopsOptions.map((numberOfStops, index) => {
    const getLable = () => {
      switch (numberOfStops) {
        case 0:
          return t("core-disruption.disruptionUniversalExercise.rebook.direct");
        case 1:
          return t(
            "core-disruption.disruptionUniversalExercise.rebook.stopsSingle",
            {
              stops: numberOfStops,
            }
          );
        default:
          return t("core-disruption.disruptionUniversalExercise.rebook.stops", {
            stops: numberOfStops,
          });
      }
    };

    return (
      <DisruptionExerciseCheckbox
        label={getLable()}
        onChange={(selected) => {
          handleStopsSelection(selected, numberOfStops);
        }}
        selected={selectedStops.includes(numberOfStops)}
        key={index}
      />
    );
  });

  const getDateLabel = (value: number) => {
    return dayjs()
      .hour(0)
      .minute(value)
      .format(brand.timeFormat || "hh:mm A");
  };

  const getHoursString = (value: number) => {
    if (value === 1) {
      return t(
        "core-disruption.disruptionUniversalExercise.rebook.hoursSingular",
        {
          hours: value,
        }
      );
    } else {
      return t("core-disruption.disruptionUniversalExercise.rebook.hours", {
        hours: value,
      });
    }
  };

  return (
    <DisruptionExerciseContainer
      content={
        <div className={cn("rebookShopFilters")}>
          <div className={cn("title")}>
            {t(
              "core-disruption.disruptionUniversalExercise.rebook.flightShopPage.filters.title"
            )}
          </div>

          {headerContent}

          <DisruptionExpandableContainer
            defaultExpanded={defaultFiltersExpanded}
            title={
              <span className={cn("sectionTitle")}>
                {t(
                  "core-disruption.disruptionUniversalExercise.rebook.flightShopPage.filters.stops"
                )}
              </span>
            }
            content={
              <div className={cn("stopsFilters")}>
                <br></br>
                {stopsFilters}
              </div>
            }
          />
          <DisruptionExpandableContainer
            defaultExpanded={defaultFiltersExpanded}
            title={
              <span className={cn("sectionTitle")}>
                {t(
                  "core-disruption.disruptionUniversalExercise.rebook.flightShopPage.filters.times"
                )}
              </span>
            }
            content={
              <div className={cn("slidersContainer")}>
                <br></br>
                <DisruptionExerciseTimeWindowSlider
                  onChange={handleDepartureTimeChange}
                  sliderMax={TIME_RANGE_MAX}
                  type="doubleThumb"
                  timeRange={departureTimeRange}
                  label={t(
                    "core-disruption.disruptionUniversalExercise.rebook.flightShopPage.filters.departureInOrigin",
                    {
                      origin: origin,
                    }
                  )}
                  getLabel={getDateLabel}
                  sliderMin={0}
                />
                <DisruptionExerciseTimeWindowSlider
                  onChange={handleArrivalTimeChange}
                  sliderMax={TIME_RANGE_MAX}
                  type="doubleThumb"
                  timeRange={arrivalTimeRange}
                  label={t(
                    "core-disruption.disruptionUniversalExercise.rebook.flightShopPage.filters.arrivalInDestination",
                    {
                      destination: destination,
                    }
                  )}
                  getLabel={getDateLabel}
                  sliderMin={0}
                />
                <DisruptionExerciseTimeWindowSlider
                  sliderMax={flightDurationRange.max}
                  onChange={handleFlightDurationChange}
                  type="doubleThumb"
                  timeRange={flightDurationRange}
                  label={t(
                    "core-disruption.disruptionUniversalExercise.rebook.flightShopPage.filters.flightDuration"
                  )}
                  getLabel={getHoursString}
                  sliderMin={flightDurationRange.min}
                />
                <DisruptionExerciseTimeWindowSlider
                  onChange={handleLayoverDurationChange}
                  sliderMax={layoverDurationRange.max}
                  type="doubleThumb"
                  timeRange={selectedLayoverDurationRange}
                  label={t(
                    "core-disruption.disruptionUniversalExercise.rebook.flightShopPage.filters.layoverDuration",
                    {
                      destination: destination,
                    }
                  )}
                  getLabel={getHoursString}
                  sliderMin={layoverDurationRange.min}
                />
              </div>
            }
          />
          <DisruptionExpandableContainer
            defaultExpanded={defaultFiltersExpanded}
            title={
              <span className={cn("sectionTitle")}>
                {t(
                  "core-disruption.disruptionUniversalExercise.rebook.flightShopPage.filters.airlines"
                )}
              </span>
            }
            content={
              <div className={cn("airlinesFilter")}>
                <br></br>
                {airlinesFilter}
              </div>
            }
          />
        </div>
      }
    />
  );
};

export default DisruptionExerciseShopFilter;
